import { Copy } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Отметка строк списка галочками и то, что с отмеченным можно унести.
 *
 * Разметка отметки ОДНА на все списки склада — «Короба» и оба списка мест
 * раздела «Баркод». Копия этой полосы разошлась бы с оригиналом на первой же
 * правке, а расходиться ей нельзя: галочка, ведущая себя на соседнем экране
 * иначе, — это не мелочь интерфейса, а сбитая привычка руки.
 *
 * Чистая половина отметки (множество ключей, диапазон, склейка списка) живёт в
 * `lib/select.ts` и проверяется тестами без браузера; здесь только разметка.
 */

/** Одна кнопка «унести отмеченное»: чем зовётся и что копирует. */
export type MarkAction = {
  label: string;
  title: string;
  /** Метка для «разметки» — по ней замечание из кабинета адресует кнопку. */
  annot: string;
  onClick: () => void;
  /**
   * Не «унести», а СДЕЛАТЬ — например, «Создать задачи» подсорта. Своя иконка
   * вместо копирования: кнопка, выглядящая как копирование, обещала бы
   * безобидное действие там, где уходит запись.
   */
  icon?: ReactNode;
  /** Погашена сверх «ничего не отмечено» — причина тогда живёт в `title`. */
  disabled?: boolean;
};

/**
 * Полоса отметки: сколько строк выбрано и что с ними можно сделать.
 *
 * Стоит ВСЕГДА, а не появляется по первой галочке. Кнопка, которой нет, пока
 * не сделаешь правильное движение, не находится вовсе, — а строка подсказки
 * заодно объясняет диапазон, о котором иначе никто не догадается.
 */
export function MarkBar({
  marked,
  allMarked,
  hint,
  flash,
  onAll,
  onNone,
  actions,
}: {
  /** Сколько строк отмечено — уже словами: «Отмечено 3 короба». */
  marked: string;
  allMarked: boolean;
  hint: string;
  flash: string;
  onAll: () => void;
  onNone: () => void;
  actions: readonly MarkAction[];
}) {
  const btn =
    "inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] border border-border px-2.5 text-[12.5px] hover:bg-bg-hover disabled:opacity-45 disabled:hover:bg-transparent";
  const empty = marked === "";
  return (
    <div
      data-annot="отметка"
      className="mb-2 flex flex-wrap items-center gap-2 text-[12.5px]"
    >
      {empty ? (
        <span className="text-fg-muted">{hint}</span>
      ) : (
        <span className="font-medium">{marked}</span>
      )}

      {allMarked ? null : (
        <button
          type="button"
          onClick={onAll}
          className="text-[12px] text-fg-muted underline underline-offset-2 hover:text-fg"
        >
          отметить все показанные
        </button>
      )}
      {empty ? null : (
        <button
          type="button"
          onClick={onNone}
          className="text-[12px] text-fg-muted underline underline-offset-2 hover:text-fg"
        >
          снять
        </button>
      )}

      {/* Ответ на нажатие: буфер обмена невидим, и без строки «Баркоды
          коробов: 12» промах по кнопке неотличим от удачи. */}
      {flash ? (
        <span data-annot="итог копирования" className="text-ok">
          {flash}
        </span>
      ) : null}

      <span className="ml-auto flex flex-wrap items-center gap-2">
        {actions.map((one) => (
          <button
            key={one.annot}
            type="button"
            disabled={empty || one.disabled}
            onClick={one.onClick}
            title={one.title}
            data-annot={one.annot}
            className={btn}
          >
            {one.icon ?? <Copy size={13} className="flex-none" />}
            {one.label}
          </button>
        ))}
      </span>
    </div>
  );
}

/**
 * Галочка «все показанные» в шапке таблицы.
 *
 * Половина отмеченного — своё состояние галочки, и ставится оно только из
 * кода: атрибута у него нет.
 */
export function MarkAll({
  all,
  some,
  title,
  onToggle,
}: {
  all: boolean;
  some: boolean;
  title: string;
  onToggle: () => void;
}) {
  return (
    <input
      type="checkbox"
      checked={all}
      ref={(el) => {
        if (el) el.indeterminate = some && !all;
      }}
      onChange={onToggle}
      title={title}
      aria-label={title}
      className="size-[15px] cursor-pointer accent-accent"
    />
  );
}

/**
 * Ячейка с галочкой строки.
 *
 * Клик ловится и самой галочкой, и ячейкой вокруг неё: отметить десять строк
 * подряд, попадая мышью в пятнадцать пикселей, — работа, которой на складе
 * никто делать не должен. Двойного срабатывания при этом нет — клик по самой
 * галочке ячейка узнаёт по `target` и пропускает.
 *
 * `select-none` тут не косметика: без него Shift-клик выделяет текст между
 * строками, и вместо отметки человек получает синюю простыню.
 *
 * Всплытие клика гасится ЗДЕСЬ: строка списка мест сама по себе кликабельна
 * (она сужает ленту движений), и без этого одно нажатие делало бы два дела.
 */
export function MarkCell({
  label,
  on,
  onPick,
}: {
  /** Что именно отмечают: «Отметить короб 7261». */
  label: string;
  on: boolean;
  onPick: (e: React.MouseEvent) => void;
}) {
  const hit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPick(e);
  };
  return (
    <td
      data-label="Отметить"
      data-annot="галочка"
      onClick={(e) => {
        if ((e.target as HTMLElement).tagName !== "INPUT") hit(e);
      }}
      className={
        "flex select-none items-center justify-between gap-3 border-b border-border/60 py-1 last:border-0 " +
        "before:text-[11px] before:uppercase before:tracking-[0.04em] before:text-fg-muted before:content-[attr(data-label)] " +
        "md:table-cell md:w-9 md:cursor-pointer md:border-0 md:px-2.5 md:py-1.5 md:text-center md:before:content-none"
      }
    >
      <input
        type="checkbox"
        checked={on}
        onChange={() => {}}
        onClick={hit}
        aria-label={label}
        className="size-[15px] cursor-pointer accent-accent"
      />
    </td>
  );
}
